import React, { useEffect, useRef, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setUser } from '../../actions';
import { FirebaseContext } from '../../services/firebase';
import { message } from '../Message';
import { v4 } from 'uuid';

const RANDOM_CODE = Math.floor(Math.random() * 900000) + 100000;
const RANDOM_TRIAL_CODE = Math.floor(Math.random() * 900000) + 100000;

const initialUserData = {
  uid: null,
  role: "USER",
  email: "",
  joinedDate: new Date().getTime(),
  lastLoginDate: null,
  firstName: "",
  lastName: "",
  phone: null,
  phoneCode: 84,
  phoneShort: "VN",
  profilePicUrl: null,
  companyName: null,
  country: null,
  birthDate: null,
  gender: null,
  address: null,
  city: null,
  status: "incomplete",
  isActivated: true,
  hasSubscription: false,
  emailVerified: false,
  proTrial: process.env.REACT_APP_PRO_TRIAL,
  basicTrial: process.env.REACT_APP_BASIC_TRIAL,
  upscaleTrial: process.env.REACT_APP_UPSCALE_TRIAL,
  pro: false,
  basic: true,
  trial: true,
  monthlyBasicCode: `REOK ${RANDOM_CODE}`,
  yearlyBasicCode: `YB ${RANDOM_CODE}`,
  monthlyProCode: `MP ${RANDOM_CODE}`,
  yearlyProCode: `YP ${RANDOM_CODE}`,
  trialCode: `REOK ${RANDOM_TRIAL_CODE}`,
  proCredits: 0,
  basicCredits: 30,
  refCode: ''
};

function GoogleOneTap({ autoPrompt = true, onSuccess, onError }) {
  const isInitialized = useRef(false);
  const user = useSelector(state => state.user);
  const dispatch = useDispatch();
  const firebase = useContext(FirebaseContext);
  const navigate = useNavigate();

  useEffect(() => {
    // Don't initialize if already signed in
    if (user) {
      return;
    }

    // Don't initialize if Firebase is not ready
    if (!firebase) {
      return;
    }

    // Don't initialize multiple times
    if (isInitialized.current) {
      return;
    }

    const handleCredentialResponse = async (response) => {
      try {
        const credential = response.credential;
        
        // Sign in to Firebase with Google credential
        const result = await firebase.signInWithGoogleOneTap(credential);
        
        // Check if user exists
        const userSnapshot = await firebase.getUser(result.user.uid);
        
        if (userSnapshot.data()) {
          // Existing user - just sign in
          const userData = userSnapshot.data();
          dispatch(setUser(userData));
          
          message.success('Successfully signed in with Google!');
          
          // Redirect to app after successful sign-in
          setTimeout(() => {
            navigate('/app');
          }, 500);
          
          if (onSuccess) {
            onSuccess(userData);
          }
        } else {
          // New user - create profile
          const refCode = localStorage.getItem('refCode') || '';
          
          const newUserData = {
            ...initialUserData,
            uid: result.user.uid,
            email: result.user.email,
            firstName: result.user.displayName || '',
            emailVerified: true,
            profilePicUrl: result.user.photoURL,
            refCode: refCode
          };
          
          // Add user to Firestore
          await firebase.addUser(result.user.uid, newUserData);
          
          // Create affiliate member
          const randomUUID = v4();
          const refLink = `https://reok.pro/vi/?ref=${randomUUID}`;
          const affData = {
            uid: result.user.uid,
            email: result.user.email,
            code: randomUUID,
            refLink: refLink,
            isActive: true,
            createdAt: new Date().toISOString()
          };
          
          await firebase.addAffMembers(affData);
          
          // Get updated user data
          const updatedUserSnapshot = await firebase.getUser(result.user.uid);
          dispatch(setUser(updatedUserSnapshot.data()));
          
          message.success('Welcome! Your account has been created.');
          
          // Redirect to app after successful sign-up
          setTimeout(() => {
            navigate('/app');
          }, 500);
          
          if (onSuccess) {
            onSuccess(updatedUserSnapshot.data());
          }
        }
        
      } catch (error) {
        console.error('Google One Tap error:', error);
        
        if (onError) {
          onError(error);
        }
        
        if (error.code === 'auth/popup-closed-by-user') {
          // User closed the popup - don't show error
          return;
        }
        
        if (error.code === 'auth/cancelled-popup-request') {
          // Cancelled popup - don't show error
          return;
        }
        
        message.error('Failed to sign in with Google. Please try again.');
      }
    };

    // Wait for Google Identity Services to load
    const initializeGoogleOneTap = () => {
      // Check if client ID is set first
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

      // Validate client ID exists
      if (!clientId) {
        console.error('Google One Tap: REACT_APP_GOOGLE_CLIENT_ID is not configured in environment variables');
        return;
      }

      if (!window.google || !window.google.accounts) {
        // Script not loaded yet, try again
        setTimeout(initializeGoogleOneTap, 100);
        return;
      }

      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: false,
          context: 'signin',
        });

        // Auto prompt if enabled
        if (autoPrompt) {
          window.google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
              // One Tap was not displayed - this is normal in many cases
              const reason = notification.getNotDisplayedReason();
              if (reason && reason !== 'opt_out_or_no_session') {
                console.log('Google One Tap not displayed:', reason);
              }
            }
          });
        }

        isInitialized.current = true;
      } catch (error) {
        console.error('Failed to initialize Google One Tap:', error);
      }
    };

    initializeGoogleOneTap();

    // Cleanup
    return () => {
      if (window.google && window.google.accounts && window.google.accounts.id) {
        try {
          window.google.accounts.id.cancel();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [user, autoPrompt, firebase, dispatch, navigate, onSuccess, onError]);

  // This component doesn't render anything visible
  // The One Tap UI is rendered by Google's SDK
  return null;
}

export default GoogleOneTap;
